<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      :class="modalClassName"
      transition="modal"
      @mousedown="handleMouseDown"
    >
      <div
        :class="modalContainerClassName"
        @mouse.stop
        @mousedown="event => event.stopPropagation()"
      >
        <!--
          Named. It is an icon with no text, so without this every dialog in the product has a
          close control that a screen reader announces as "button" and nothing more.
        -->
        <woot-button
          v-if="showCloseButton"
          :aria-label="$t('GENERAL_SETTINGS.CLOSE_DIALOG')"
          color-scheme="secondary"
          icon="dismiss"
          variant="clear"
          class="absolute end-2 top-2 z-10"
          @click="close"
        />
        <slot />
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  props: {
    closeOnBackdropClick: {
      type: Boolean,
      default: true,
    },
    show: Boolean,
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    modalType: {
      type: String,
      default: 'centered',
    },
    size: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      mousedDownOnBackdrop: false,
    };
  },
  computed: {
    modalContainerClassName() {
      let className =
        'modal-container bg-white dark:bg-slate-800 skip-context-menu';
      if (this.fullWidth) {
        return `${className} modal-container--full-width`;
      }

      return `${className} ${this.size}`;
    },
    modalClassName() {
      const modalClassNameMap = {
        centered: '',
        'right-aligned': 'right-aligned',
      };

      return `modal-mask skip-context-menu ${
        modalClassNameMap[this.modalType] || ''
      }`;
    },
  },
  mounted() {
    // A NAMED handler, so `beforeDestroy` can take it back off again.
    //
    // This used to be an inline arrow, which meant nothing held a reference to it and
    // `removeEventListener` had nothing to pass — so every modal in the app left one live
    // `document` keydown listener behind for the life of the tab, and this component is used in
    // 84 files. The closure also keeps `this` alive, so a modal destroyed while open leaves a
    // handler that still answers Escape by calling `onClose` on a component nobody can see.
    document.addEventListener('keydown', this.onKeydown);
    document.body.addEventListener('mouseup', this.onMouseUp);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.removeEventListener('mouseup', this.onMouseUp);
  },
  methods: {
    onKeydown(e) {
      if (this.show && e.code === 'Escape') {
        this.onClose();
      }
    },
    handleMouseDown() {
      this.mousedDownOnBackdrop = true;
    },
    close() {
      this.onClose();
    },
    onMouseUp() {
      if (this.mousedDownOnBackdrop) {
        this.mousedDownOnBackdrop = false;
        this.onClose();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.modal-container--full-width {
  align-items: center;
  border-radius: 0;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

// `right-aligned` is the public value of the `modalType` prop, not a side: `flex-end`
// already resolves against the writing direction, so the drawer opens on the trailing
// edge — the right in English, the left in Arabic. Renaming the prop would break callers.
.modal-mask.right-aligned {
  justify-content: flex-end;

  .modal-container {
    border-radius: 0;
    height: 100%;
    width: 30rem;
  }
}
.modal-big {
  width: 60%;
}
</style>
